import { allCurrencies, getColumnShape } from '@squaredup/data-streams';
import { hasHandlebarsPlaceholders } from '@squaredup/utilities';
import Field from 'components/forms/field/Field';
import type { AutocompleteOption } from 'components/forms/jsonForms/autocompleteOptions';
import { partition } from 'lodash';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { DataStreamMetadataStateColumn } from '../MetadataEditorState';

export const CurrencyShapeConfigFields: React.FC<{ state: DataStreamMetadataStateColumn | undefined }> = ({
    children,
    state
}) => {
    const form = useFormContext();

    /**
     * Default code (from the data stream configuration) may use placeholders,
     * e.g. `{{ column.currencyCodeColumn }}`, so we need to handle that case.
     */
    const defaultCode =
        state == null || state.isPattern
            ? form.formState.defaultValues?.code
            : getColumnShape((state.definitions ?? []).find((d) => d.source !== 'request')?.definition.shape)?.config
                  .code;

    const [commonCurrencies, otherCurrencies] = partition([...allCurrencies.values()], (c) =>
        ['usd', 'eur', 'gbp'].includes(c.code)
    );

    otherCurrencies.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));

    const currencyToOption = (
        c: (typeof commonCurrencies)[number]
    ): AutocompleteOption & { labelElement: ReactNode } => {
        const exampleFormattedValue = c.format(12.34);
        return {
            label: c.displayName,
            labelElement: (
                // Min width ensures a gap between display name and sample value when selected
                <div className='flex justify-between min-w-[12rem]'>
                    <span>{c.displayName}</span>
                    <span className='text-sm text-textSecondary'>{exampleFormattedValue}</span>
                </div>
            ),
            value: c.code
        };
    };

    const commonCurrencyOptions = commonCurrencies.map(currencyToOption);
    const otherCurrencyOptions = otherCurrencies.map(currencyToOption);
    const dynamicCurrencyOptions = hasHandlebarsPlaceholders(defaultCode)
        ? [
              currencyToOption({
                  code: defaultCode,
                  displayName: 'From currency column',
                  format: () => ''
              })
          ]
        : [];

    const hasDynamicCurrencyOption = dynamicCurrencyOptions.length > 0;

    return (
        <>
            <Field.Input
                label='Currency'
                name='code'
                type='autocomplete'
                isMulti={false}
                menuPosition='absolute'
                selectOptionsAs={'valueString'}
                className='w-80'
                help={
                    'Currency to format the values as. This does not apply any currency conversion.' +
                    (hasDynamicCurrencyOption
                        ? " 'From currency column' uses a different currency code for each row based on the currency column."
                        : '')
                }
                formatOptionLabel={(o: { labelElement: ReactNode }) => o.labelElement}
                isSearchable={true}
                options={[
                    ...(hasDynamicCurrencyOption ? [{ label: 'Dynamic', options: dynamicCurrencyOptions }] : []),
                    { label: 'Common', options: commonCurrencyOptions },
                    { label: 'Other', options: otherCurrencyOptions }
                ]}
            />
            {children}
        </>
    );
};
